import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import groupBy from 'lodash/groupBy';
import get from 'lodash/get';
import filter from 'lodash/filter';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { Row, Container } from 'reactstrap';

import Meta from 'Components/Utility/SEO/Meta';
import HitCard from 'Components/Cards/HitCard';
import SkeletonHitCard from 'Components/Cards/SkeletonHitCard';
import PriceContainer from 'Containers/Utility/Price/PriceContainer';
import Paginate from 'Components/Utility/Paginate';
import { fetchFacetFeatureQuery } from 'Redux/ducks/algolia-calls';
import NotFound from 'Components/NotFound/NotFound';
import getUrlParameter from 'Helpers/Url/GetUrlParameter';
import { encodeSlug } from 'Helpers/Slug';
import useViewport from 'Hooks/useViewport';
import NoIndex from 'Components/Utility/SEO/NoIndex';
import meta from 'Components/FacetedFacetSearch/facetFilterMeta';
import FluidBackgroundImg from 'Components/Utility/FluidBackgroundImg';
import FluidImg from 'Components/Utility/FluidImg';
import FacetBreadcrumbs from 'Components/Utility/SEO/FacetBreadcrumbs';
import VrSearchFilters from 'Components/FacetedFacetSearch/VrSearchFilters';
import PopupHitCard from 'Components/Cards/PopupHitCard';
import { IsChallenger } from 'Redux/ducks/split-test';

import 'Components/Banner/CategoryPageBanner.scss';
import 'Components/StaticSearch/StaticFilters.scss';


const messages = defineMessages({
  pageNumber: {
    id: 'feature-search.page-number',
    defaultMessage: 'Page {pageNumber}',
  },
  showFilters: {
    id: 'feature-search.show-filters',
    defaultMessage: 'Show VR Filters',
  },
  hideFilters: {
    id: 'feature-search.hide-filters',
    defaultMessage: 'Hide VR Filters',
  },
});

export default function FacetedFeatureSearch({ location, history, match }) {
  const intl = useIntl();
  const { formatMessage } = intl;
  const { isBelowSm, isAboveLg } = useViewport();

  const slug = match.params.facet;

  if (!meta[slug]) {
    throw new Error('Slug not found');
  }

  const metaObj = meta[slug];

  if (!metaObj.facets) {
    metaObj.facets = [];
  }

  const page = Number(getUrlParameter('page', location.search) || 1);

  const urlFacets = (match.params.facets || '').split('/').filter(a => a).map(str => str.replace(/-/g, '_'));
  const validFacets = metaObj.facets.map(f => f.key);

  const initialFacetObject = {};
  metaObj.facets.forEach((f) => {
    initialFacetObject[f.key] = {
      field: f.field || metaObj.defaultFacetField,
      title: f.title,
      key: f.key,
      segment: f.segment,
      language: f.language,
      selected: urlFacets.includes(f.key),
    };
  });

  const [isLoading, setIsLoading] = useState(true);
  const [facets, setFacets] = useState(initialFacetObject);
  const [results, setResults] = useState({
    hits: null,
    page: null,
    nbPages: null,
    nbHits: null,
    hitsPerPage: null,
    facets: null,
  });
  const [showFilters, setshowFilters] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const facetsWithHits = filter(facets,
    ((facet, key) => results.facets &&
      results.facets[facet.field] &&
      results.facets[facet.field] &&
      results.facets[facet.field][key]),
  );

  useEffect(() => {
    if (isBelowSm) {
      setshowFilters(false);
    }
    const fetchHits = async () => {
      if (metaObj.facetFilter) {
        const searchResults =
          await fetchFacetFeatureQuery(metaObj.facetFilter, Object.values(facets), page - 1);
        setResults(searchResults);
      }

      setIsLoading(false);
    };

    fetchHits().catch(setFetchError);
  }, [page, slug, facets, metaObj, isBelowSm]);

  if (fetchError) {
    throw fetchError;
  }

  if (!slug || !meta[slug]) {
    return <NotFound />;
  }

  if (urlFacets.some(f => !validFacets.includes(f))) {
    return <NotFound />;
  }

  if (!isLoading && (!results || !results.hits || !results.hits.length)) {
    return <NotFound />;
  }

  const onChange = (e) => {
    const key = e.target.name;
    const value = e.target.checked;
    const facet = facets[e.target.name];

    const baseUrl = location.pathname.split('/').slice(0, 2).join('/');

    const newUrlFacets = [...urlFacets].filter(f => f !== key);

    if (value) {
      newUrlFacets.push(key);
    }

    const newPath = `${baseUrl}${newUrlFacets.length ? '/' : ''}${newUrlFacets.sort((a, b) => validFacets.indexOf(a) - validFacets.indexOf(b)).map(str => str.replace(/_/g, '-')).join('/')}`;

    setIsLoading(true);
    setFacets({ ...facets, [key]: { ...facet, selected: value } });
    history.push({ pathname: newPath, search: '' });
  };

  const selectedFacets = Object.values(facets).filter(f => f.selected);

  const facetGroups = groupBy(facetsWithHits, 'segment');
  const facetsGroupsArray = Object.keys(facetGroups)
    .map(segment => ({
      name: segment,
      facets: facetGroups[segment],
    }))
    .filter(segment => segment.facets.length);

  const pageTitle = `${selectedFacets.length === 1 ? `${selectedFacets[0].title} ` : ''}${formatMessage(metaObj.language.title)}${page > 1 ? ' - ' : ''}${page > 1 ? formatMessage(messages.pageNumber, { pageNumber: page }) : ''}`;
  const seoTitle = `${selectedFacets.length === 1 ? `${selectedFacets[0].title} ` : ''}${formatMessage(metaObj.language.seoTitle)}${page > 1 ? ' | ' : ''}${page > 1 ? formatMessage(messages.pageNumber, { pageNumber: page }) : ''} | Fanatical`;

  let paragraph = metaObj.language.paragraph ? formatMessage(metaObj.language.paragraph) : '';
  let seoDesc = metaObj.language.seoDesc ? formatMessage(metaObj.language.seoDesc) : '';

  const filterParagraph = get(selectedFacets, '[0].language.paragraph');

  if (filterParagraph && selectedFacets.length === 1) {
    paragraph = formatMessage(filterParagraph);
    seoDesc = formatMessage(filterParagraph);
  }

  const noIndex = urlFacets.length > 1 && !urlFacets.every(f => f.startsWith('oculus_meta_quest'));

  const HitCardComponent = isAboveLg && IsChallenger('PopupHitCardV2') ? PopupHitCard : HitCard;


  return (
    <div>
      <Meta
        title={seoTitle}
        description={seoDesc}
        url={selectedFacets.length === 1 ? `${slug}/${encodeSlug(selectedFacets[0].key)}` : `${slug}`}
        hits={results.hits}
        nbHits={results.nbHits}
        hitsPerPage={results.hitsPerPage}
      />
      <FacetBreadcrumbs
        slug={slug}
        name={formatMessage(metaObj.language.title)}
        subSlug={encodeSlug(get(selectedFacets, '[0].key', ''))}
        subName={get(selectedFacets, '[0].title')}
        page={page}
      />

      {noIndex &&
        <NoIndex />
      }
      {slug === 'vr-games' &&
        <FluidBackgroundImg
          className="category-page-banner"
          folder="assets"
          subfolder="banners/takeover"
          src="VR-Games-TAKEOVER.jpg"
          heights={{ xs: 120, sm: 120, md: 143, lg: 170, xl: 170, xxl: 200 }}
          fit="crop"
          tagName="header"
          bgImgFluid={false}
        >
          <Container>
            <div className="title-sub-container">
              <div className="title-container">
                <div className="vr-logo">
                  <FluidImg
                    folder="assets"
                    subfolder="misc"
                    src="vr-logo-icon.png"
                    alt="VR Games"
                  />
                </div>
                <span className="books">
                  <FormattedMessage id="vr.hero.title.span" defaultMessage="games" />
                </span>
              </div>
              <div className="subtitle-container">
                <div className="subtype-container">
                  <FormattedMessage id="vr.hero.subtype.immersive" defaultMessage="Immersive Worlds" />
                </div>
                <div className="divider" />
                <div className="subtype-container">
                  <FormattedMessage id="vr.hero.subtype.intense" defaultMessage="Intense Experiences" />
                </div>
                <div className="divider" />
                <div className="subtype-container">
                  <FormattedMessage id="vr.hero.subtype.unmissable" defaultMessage="Unmissable Games" />
                </div>
              </div>
            </div>
          </Container>
        </FluidBackgroundImg>
      }
      <Container>
        <div className={classNames({ 'd-none': slug === 'vr-games' })}>
          <h1 className="pt-5 mb-3">{pageTitle}</h1>
        </div>
        {!!paragraph &&
          <p className="seo-blurb-text mb-5">
            {paragraph}
          </p>
        }
        {!!facetsGroupsArray.length && slug !== 'vr-games' &&
          <div className="categories-and-sortby-section">
            <nav data-nosnippet className="categories-container w-100">
              {facetsGroupsArray.map(segment =>
                <div className="facet-segment w-33" key={segment.name}>
                  {segment.name && metaObj.segmentTitles && metaObj.segmentTitles[segment.name] &&
                    <span className="facet-segment-title">{formatMessage(metaObj.segmentTitles[segment.name])}</span>
                  }
                  <div className="d-flex dynamic-checkboxes-container">
                    {segment.facets.map(facet =>
                      <div className="checkbox-container" key={facet.key}>
                        <Link
                          to={`${slug}/${encodeSlug(facet.key)}`}
                          className="d-none"
                        >
                          {formatMessage(metaObj.language.title)} {facet.title}
                        </Link>
                        <input
                          id={facet.key}
                          name={facet.key}
                          type="checkbox"
                          checked={facet.selected}
                          aria-label={facet.key}
                          onChange={onChange}
                        />
                        <label htmlFor={facet.key} className="ml-1">{facet.title}</label>
                      </div>,
                    )}
                  </div>
                </div>,
              )}
            </nav>
          </div>
        }
        {!!facetsGroupsArray.length && slug === 'vr-games' &&
          <React.Fragment>
            <div className="d-md-none">
              <button className="btn btn-secondary btn-black btn-block mb-3" onClick={() => { setshowFilters(!showFilters); }}>
                {!showFilters && formatMessage(messages.showFilters)}
                {showFilters && formatMessage(messages.hideFilters)}
              </button>
            </div>
            {showFilters &&
              <VrSearchFilters facetsGroupsArray={facetsGroupsArray} onChange={onChange} />
            }
          </React.Fragment>
        }
        {isLoading &&
          <Row>
            <SkeletonHitCard
              numberOfCards={36}
              lgSize={4}
            />
          </Row>
        }
        {!isLoading &&
          <React.Fragment>
            <Row>
              {results.hits.map((hit, i) =>
                <HitCardComponent
                  lazy={i >= 6}
                  key={hit.objectID}
                  hit={hit}
                  lgSize={4}
                >
                  <PriceContainer
                    discountPercent={hit.discount_percent}
                    price={hit.price}
                    tiered={hit.tiered}
                    slug={hit.slug}
                    hideDiscount={hit.hide_discount}
                    payWhatYouWant={hit.pay_what_you_want}
                  />
                </HitCardComponent>,
              )}
            </Row>
            <Row>
              <Paginate hideFirstLast hits={results} updateHits={() => { }} />
            </Row>
          </React.Fragment>
        }
      </Container>
    </div>
  );
}

FacetedFeatureSearch.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }).isRequired,
  match: PropTypes.shape({}).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
};
